<template>
    <div class="forms-signatures">
        <shield-modal
        :id="'signatures-modal'"
        class="forms-signatures"
        :size="'md'"
        @handleSubmit="sign"
        @handleCancel="handleHidden"
        :enableSubmit="!isInvalid"
        :noClose="true">
            
            <template #title>
                Sign Your Forms
            </template>
            <template #default>
                <page-loader :loading="saving" />
                <forms-signature-content 
                    :primarySeller="{
                        legalName: legalName,
                        signatureDate: signatureDate,
                        legalNameValidationState: legalNameValidationState,
                        signatureDateValidationState: signatureDateValidationState,
                    }"
                    :secondarySeller="{
                        secondaryLegalName: secondaryLegalName,
                        secondarySignatureDate: secondarySignatureDate,
                        secondaryLegalNameValidationState: secondaryLegalNameValidationState,
                        secondarySignatureDateValidationState: secondarySignatureDateValidationState,
                    }"
                    :showPrimarySignature="showPrimarySignature"
                    :showSecondarySignature="showSecondarySignature"
                    :orderAppResource="orderAppResource"
                    @change="onInputChange"/>
            </template>

            <template #submit-btn>
                Sign All Forms
            </template>
            
            <template #cancel-btn>
                I'll Sign Later
            </template>
        </shield-modal>
        <edit-information-modal 
            :orderAppResource="orderAppResource"
            @save="handleSave"
        />
    </div>

</template>

<script>
import './FormsSignaturesModal.scss';
import FormErrorsMixin from '@sellers-shield-home-protection/ss-ui-kit/src/components/FormErrors/FormErrorsMixin';
import UserMixin from '@mixins/UserMixin';
import {
    PRIMARY_DATE,
    PRIMARY_INITIALS,
    PRIMARY_NAME,
    PRIMARY_SIGNATURE,
    SECONDARY_DATE,
    SECONDARY_INITIALS,
    SECONDARY_NAME,
    SECONDARY_SIGNATURE,
} from '@sellers-shield-home-protection/ss-core-js/src/AppResources/FormAppResource';
import {primarySeller, secondarySeller} from '@helpers/order';
import OrderAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderAppResource";
import ShieldModal from '@components/modals/ShieldModal/ShieldModal.vue';
import FormsSignatureContent from './FormsSignatureContent.vue';
import PageLoader from '../../loaders/PageLoader/PageLoader.vue';
import EditInformationModal from './EditInformationModal.vue';
import { useOrdersStore} from '@store';
import {mapActions,mapState} from 'pinia';
export default {
    name: 'FormsSignaturesModal',
    emits:['hidden', 'submit'],
    mixins: [
        FormErrorsMixin,
        UserMixin,
    ],
    components: {
        ShieldModal,
        FormsSignatureContent,
        PageLoader,
        EditInformationModal
    },
    data: () => ({
        legalName: '',
        signatureDate: null,
        legalNameValidationState: null,
        signatureDateValidationState: null,

        secondaryLegalName: '',
        secondarySignatureDate: null,
        secondaryLegalNameValidationState: null,
        secondarySignatureDateValidationState: null,

        showPrimarySignature: true,
        showSecondarySignature: false,
        requireSecondarySignature: false,
    }),
    props: {
        saving: {
            type: Boolean,
        },

        saved: {
            type: Boolean,
            default: false,
        },
        orderAppResource: {
            type: OrderAppResource,
            required: true,
        }
    },
    computed: {
        hasSecondarySeller() {
            let seller = secondarySeller(this.orderAppResource);
            return !!seller.firstName && !!seller.lastName || !!seller.email && (!!seller.uuid || !!seller.firstName || !!seller.lastName);
        },
        isInvalid() {
            let arr = [
                this.legalNameValidationState,
                this.signatureDateValidationState,
            ];
            if (this.showSecondarySignature) {
                arr = [
                    this.legalNameValidationState,
                    this.signatureDateValidationState,
                    this.secondaryLegalNameValidationState,
                    this.secondarySignatureDateValidationState,
                ];
            }
            return arr.some((state) => state === false);
        },
        confirmationModalCaption() {
            if (!this.showSecondarySignature && this.hasSecondarySeller) {
                return 'Your signature had been captured! We\'ve contacted your secondary seller to review and sign the forms.';
            } else {
                return 'Thank you for signing the documents!';
            }
        },
    },
    methods: {
        ...mapActions(useOrdersStore, {
            fetchOrders: 'fetchMyOrdersAsSeller'
        }),
        getNameInitials(fullName) {
            return (fullName || '').replace(/[^a-zA-Z- ]/g, "").match(/\b\w/g).join(' ');
        },  
        onInputChange(prop, value){
            this[prop] = value;
            this.validate();
        },
        validate() {
            if (this.showSecondarySignature) {
                this.secondaryLegalNameValidationState = this.secondaryLegalName ? null : false;
                this.secondarySignatureDateValidationState = this.secondarySignatureDate ? null : false;
            }
            this.legalNameValidationState = this.legalName ? null : false;
            this.signatureDateValidationState = this.signatureDate ? null : false;
        },

        async sign() {
            this.validate();

            if (!this.isInvalid) {

                let data = {};

                if (this.showPrimarySignature && this.getNameInitials(this.legalName)) {
                    data[PRIMARY_NAME] = this.legalName;
                    data[PRIMARY_SIGNATURE] = this.legalName;
                    data[PRIMARY_DATE] = this.signatureDate;
                    data[PRIMARY_INITIALS] = this.getNameInitials(this.legalName);
                }

                if (this.showSecondarySignature && this.getNameInitials(this.secondaryLegalName)) {
                    data[SECONDARY_NAME] = this.secondaryLegalName;
                    data[SECONDARY_SIGNATURE] = this.secondaryLegalName;
                    data[SECONDARY_DATE] = this.secondarySignatureDate;
                    data[SECONDARY_INITIALS] = this.getNameInitials(this.secondaryLegalName);
                }

                if (Object.keys(data).length > 0) {
                    this.$emit('submit', data);
                }
            }
        },
        handleHidden() {
            this.$bvModal.hide('signatures-modal');
        },
        showEditModal(){
            this.$nextTick(()=>{
                this.$bvModal.show('edit-information-modal');
            });
        },
        async handleSave(){
            await this.fetchOrders();
        }
    },
    created() {
        if (this.hasSecondarySeller) {
            let primSeller = primarySeller(this.orderAppResource),
                secSeller = secondarySeller(this.orderAppResource);

            this.showPrimarySignature = !primSeller?.userUuid || this.me?.uuid === primSeller?.userUuid;
            this.requireSecondarySignature = !Boolean(secSeller.email) && !Boolean(secSeller.uuid);
            this.showSecondarySignature = this.requireSecondarySignature;
        }
    },
    mounted(){
        this.$bus.$on('show-edit-modal', this.showEditModal);
    },
    beforeDestroy(){
        this.$bus.$off('show-edit-modal');
    }
}
</script>
